import { headersDeAtuacao } from '@/lib/delegacao/atuacao';
import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchEventSource } from '@microsoft/fetch-event-source';

import { useAuth } from './useAuth';

const MAX_RETRIES = 5;
const POLLING_INTERVAL_MS = 10_000;

function backoffMs(attempt: number): number {
  return Math.min(2 ** attempt * 2000, 30_000);
}

/** Estado mínimo de um job acompanhado por SSE (sync de clientes, import do acervo...). */
export interface JobProgressBase {
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'idle';
}

const EVENTO_PARA_STATUS: Record<string, JobProgressBase['status']> = {
  pending: 'PENDING',
  running: 'RUNNING',
  completed: 'COMPLETED',
  failed: 'FAILED',
  cancelled: 'CANCELLED',
};

/**
 * Acompanha o progresso de um job de usuário por SSE, com retry em backoff exponencial
 * (máx. 5) e fallback para polling do endpoint de status quando o SSE não se sustenta.
 *
 * Analogia Angular: um service com EventSource emitindo por BehaviorSubject; aqui é
 * useState + useEffect. O backend encerra o stream ao chegar em estado terminal.
 *
 * @param enabled    conecta quando true; desconecta quando false
 * @param streamPath caminho do SSE relativo à base da API (ex.: `/clientes/sync/stream`)
 * @param fetchStatus consulta de status usada no fallback de polling
 * @param geracao    mudar o valor reabre o stream (ex.: novo pedido depois de um estado terminal)
 * @param estadoInicial estado já conhecido, exibido enquanto o SSE não entrega o primeiro evento
 *                      (evita a espera do handshake); um valor novo não nulo sempre passa a valer
 * @returns o estado atual, ou null quando ocioso/desconectado
 */
export function useJobProgress<T extends JobProgressBase>(
  enabled: boolean,
  streamPath: string,
  fetchStatus: () => Promise<T>,
  geracao = 0,
  estadoInicial: T | null = null,
): T | null {
  // O que o stream entregou, e com qual estado inicial ele chegou (ver `jaRecebeu` no fim).
  const [recebido, setRecebido] = useState<{ valor: T | null; estadoInicial: T | null | undefined }>({
    valor: null,
    estadoInicial: undefined,
  });
  const { getToken } = useAuth();
  const retryCountRef = useRef(0);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fetchStatusRef = useRef(fetchStatus);
  useEffect(() => {
    fetchStatusRef.current = fetchStatus;
  }, [fetchStatus]);

  // Enquanto o stream não falou sobre o estado inicial vigente, é ele que a tela mostra; depois do
  // primeiro evento passa a valer o stream — inclusive um `idle`, que apaga o aviso. Cada evento
  // registra com qual estado inicial chegou: um estado inicial novo (outro pedido) volta a mandar.
  const estadoInicialRef = useRef(estadoInicial);
  useEffect(() => {
    estadoInicialRef.current = estadoInicial;
  }, [estadoInicial]);
  const publicar = useCallback((valor: T | null) => {
    setRecebido({ valor, estadoInicial: estadoInicialRef.current });
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPollingFallback = useCallback(() => {
    stopPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const data = await fetchStatusRef.current();
        const s = (data.status ?? 'idle').toUpperCase();
        publicar(s === 'IDLE' ? null : ({ ...data, status: s } as T));
        if (s === 'COMPLETED' || s === 'FAILED' || s === 'CANCELLED') {
          stopPolling();
        }
      } catch {
        // Erro no polling — será retentado no próximo intervalo
      }
    }, POLLING_INTERVAL_MS);
  }, [publicar, stopPolling]);

  useEffect(() => {
    if (!enabled) {
      stopPolling();
      return;
    }

    let cancelled = false;
    abortRef.current = new AbortController();

    const connect = async () => {
      if (cancelled) return;

      try {
        const token = await getToken();
        const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';

        await fetchEventSource(`${baseUrl}${streamPath}`, {
          signal: abortRef.current!.signal,
          headers: { Authorization: `Bearer ${token}`, ...headersDeAtuacao(`${baseUrl}${streamPath}`) },
          onmessage(ev) {
            // Mensagem recebida — resetar contador de retry
            retryCountRef.current = 0;
            try {
              const data = JSON.parse(ev.data);
              if (ev.event === 'idle') {
                publicar(null);
                abortRef.current?.abort();
                return;
              }
              const status = EVENTO_PARA_STATUS[ev.event];
              if (!status) return;
              publicar({ ...data, status } as T);
              if (status === 'COMPLETED' || status === 'FAILED' || status === 'CANCELLED') {
                abortRef.current?.abort();
              }
            } catch {
              // Payload inválido — ignorar evento
            }
          },
          onerror() {
            retryCountRef.current += 1;

            if (retryCountRef.current > MAX_RETRIES) {
              // SSE esgotou retries — cair para polling
              abortRef.current?.abort();
              startPollingFallback();
              return;
            }

            // Lançar para cancelar a reconexão automática da lib
            // e fazer retry manual com backoff no catch abaixo
            throw new Error('retry');
          },
        });
      } catch {
        // Conexão fechada, abortada ou erro de retry
        if (cancelled) return;

        if (retryCountRef.current <= MAX_RETRIES) {
          const delay = backoffMs(retryCountRef.current);
          setTimeout(() => {
            if (!cancelled) {
              abortRef.current = new AbortController();
              connect();
            }
          }, delay);
        } else {
          startPollingFallback();
        }
      }
    };

    connect();

    return () => {
      cancelled = true;
      abortRef.current?.abort();
      abortRef.current = null;
      stopPolling();
    };
  }, [enabled, streamPath, geracao, getToken, publicar, startPollingFallback, stopPolling]);

  // Enquanto o stream não falou sobre o estado inicial vigente, quem manda é ele.
  const jaRecebeu = recebido.estadoInicial === estadoInicial;
  return jaRecebeu ? recebido.valor : (estadoInicial ?? recebido.valor);
}
